import i18n from '@/utils/i18n';
import React from 'react';
import { createStyles } from '@/contexts/theme-context';
import {
  formatCompactCurrency,
  formatCompactNumber,
} from '@sales-portal/blocs/utils/money';
import {
  getDeltaAttributes,
  resolveDelta,
} from '@sales-portal/domain/pricing/screens/management/blocs/utils/table-ui';
import { Typography, TypographyVariant } from '@usepepper/ui/atoms/typography';
import { View } from 'react-native';

interface Props {
  costChangeImpact?: number;
  currencyCode: string;
  editsImpact?: number;
  editsImpactLabelKey?: string;
  headingVariant: TypographyVariant;
  isStacked?: boolean;
  recImpact?: number;
  recImpactLabelKey?: string;
  restaurantCount: number;
  totalWeeklyVolume?: number;
  variantPackCount: number;
}

export const PricingSummaryHeaderDetails: React.FC<Props> = ({
  costChangeImpact,
  currencyCode,
  editsImpact,
  editsImpactLabelKey = 'pricing.table.header.weekly_edits_impact',
  headingVariant,
  isStacked = false,
  recImpact,
  recImpactLabelKey,
  restaurantCount,
  totalWeeklyVolume,
  variantPackCount,
}) => {
  const [styles, theme] = useStyles();

  const renderImpact = (value: number, labelKey: string) => {
    const { direction, formattedValue } = resolveDelta(value, (v) =>
      formatCompactCurrency(v, currencyCode),
    );
    const { Icon, color } = getDeltaAttributes(direction, theme);
    return (
      <View style={styles.summaryItem}>
        <View style={styles.summaryImpactDelta}>
          <Typography variant={headingVariant}>{formattedValue}</Typography>
          {Icon ? (
            <Icon fill={color} height={12} stroke={color} width={12} />
          ) : null}
        </View>
        <Typography color={theme.sk.color.text.primary} variant="paragraph-md">
          {i18n.t(labelKey)}
        </Typography>
      </View>
    );
  };

  const items = (
    <>
      <View style={styles.summaryItem}>
        <Typography variant={headingVariant}>{variantPackCount}</Typography>
        <Typography color={theme.sk.color.text.primary} variant="paragraph-md">
          {i18n.t('items')}
        </Typography>
      </View>
      <View style={styles.summaryItem}>
        <Typography variant={headingVariant}>{restaurantCount}</Typography>
        <Typography color={theme.sk.color.text.primary} variant="paragraph-md">
          {i18n.t('accounts')}
        </Typography>
      </View>
      {totalWeeklyVolume !== undefined ? (
        <View style={styles.summaryItem}>
          <Typography variant={headingVariant}>
            {totalWeeklyVolume < 1000
              ? i18n.t('pricing.summary.total_volume', {
                  count: formatCompactNumber(totalWeeklyVolume),
                })
              : formatCompactNumber(totalWeeklyVolume)}
          </Typography>
          <Typography
            color={theme.sk.color.text.primary}
            variant="paragraph-md"
          >
            {i18n.t('pricing.table.header.weekly_volume')}
          </Typography>
        </View>
      ) : null}
      {costChangeImpact !== undefined
        ? renderImpact(
            costChangeImpact,
            'pricing.table.header.weekly_cost_change_gross_profit_impact',
          )
        : null}
      {typeof recImpact === 'number' && recImpactLabelKey
        ? renderImpact(recImpact, recImpactLabelKey)
        : null}
      {typeof editsImpact === 'number'
        ? renderImpact(editsImpact, editsImpactLabelKey)
        : null}
    </>
  );

  return isStacked ? (
    <View style={styles.stackedSummaryItems}>{items}</View>
  ) : (
    items
  );
};

const useStyles = createStyles((theme) => ({
  stackedSummaryItems: {
    columnGap: theme.sk.spacing.spaceXlarge,
    flexDirection: 'row',
    flexWrap: 'wrap',
    rowGap: theme.sk.spacing.spaceLarge,
  },
  summaryImpactDelta: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: theme.sk.spacing.spaceXsmall,
  },
  summaryItem: {
    alignItems: 'center',
    gap: theme.sk.spacing.spaceXsmall,
  },
}));
